import { CONFIG } from "../../config";
import { getTokenizedAxios } from "../../utils/axiosWithTokenGen";

export type MarketingCategory = "bar" | "cafe" | "restaurant";

export interface PipedriveConstants {
  /**
   * Campaign tag, emitted as a `pipedriveCampaign` column on every row. Import it
   * as a Pipedrive label so the whole promo list (matched + unmatched) becomes one
   * filterable working list / SendGrid audience.
   */
  campaign?: string;
}

export type PipedriveStatusFilter = "all" | "matched" | "unmatched";

export interface BuildSegmentParams {
  categories?: MarketingCategory[];
  useMccFallback?: boolean;
  mccs?: number[];
  country?: string;
  tpvThresholdEur?: number;
  lookbackWeeks?: number;
  minMatchConfidence?: number;
  statuses?: string[];
  mode?: "count" | "rows";
  maxRows?: number;
  pipedrive?: PipedriveConstants;
  pipedriveStatus?: PipedriveStatusFilter;
}

export interface SegmentRow {
  businessName: string;
  tradeName: string;
  category: string;
  street: string;
  postcode: string;
  city: string;
  country: string;
  contactName: string;
  email: string;
  phone: string;
  website: string;
  placeId: string;
  matchConfidence: number | "";
  tpvEur: number;
  pdMatchStatus: string;
  pdMatchConfidence: number | "";
  pipedriveOrgId: number | "";
  pipedrivePersonId: number | "";
  pipedriveUrl: string;
  [extraColumn: string]: string | number;
}

export interface BuildSegmentResult {
  mode: "count" | "rows" | "export";
  count: number;
  truncated: boolean;
  rows?: SegmentRow[];
  url?: string;
}

export interface EnrichMerchantsParams {
  limit?: number;
  mccs?: number[];
  country?: string;
  minMatchConfidence?: number;
  force?: boolean;
  dryRun?: boolean;
}

export interface EnrichMerchantsResult {
  scanned: number;
  enriched: number;
  matched: number;
  needsReview: number;
  noMatch: number;
  errors: number;
  dryRun: boolean;
}

export interface PipedriveMatchParams {
  limit?: number;
  categories?: MarketingCategory[];
  country?: string;
  statuses?: string[];
  minMatchConfidence?: number;
  force?: boolean;
  dryRun?: boolean;
}

export interface PipedriveMatchResult {
  scanned: number;
  matched: number;
  ambiguous: number;
  none: number;
  errors: number;
  dryRun: boolean;
}

export async function buildMarketingSegmentServer(
  params: BuildSegmentParams
): Promise<BuildSegmentResult> {
  const axios = await getTokenizedAxios();
  const path = "/marketing/segment";
  return axios
    .post<BuildSegmentResult>(CONFIG.MARKETING_SERVER.BASE_URL + path, params, {
      timeout: 60000,
    })
    .then((res) => res.data);
}

export async function enrichMerchantsServer(
  params: EnrichMerchantsParams
): Promise<EnrichMerchantsResult> {
  const axios = await getTokenizedAxios();
  const path = "/marketing/enrich";
  return axios
    .post<EnrichMerchantsResult>(CONFIG.MARKETING_SERVER.BASE_URL + path, params, {
      timeout: 60000,
    })
    .then((res) => res.data);
}

export async function matchPipedriveServer(
  params: PipedriveMatchParams
): Promise<PipedriveMatchResult> {
  const axios = await getTokenizedAxios();
  const path = "/marketing/pipedrive-match";
  return axios
    .post<PipedriveMatchResult>(CONFIG.MARKETING_SERVER.BASE_URL + path, params, {
      timeout: 60000,
    })
    .then((res) => res.data);
}
